import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Trash2, ArrowRight, FileText, Gauge, Wrench } from "lucide-react";

export default function History() {
  const { user, loading: authLoading } = useAuth();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    load();
    // eslint-disable-next-line
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/calc/history");
      setItems(r.data);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "sk" ? "Vymazať tento výpočet?" : "Delete this entry?")) return;
    await api.delete(`/calc/history/${id}`);
    setItems((arr) => arr.filter((i) => i.id !== id));
  };

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const linkFor = (h) => {
    const params = new URLSearchParams();
    Object.entries(h.inputs || {}).forEach(([k, v]) => params.set(k, String(v)));
    return h.type === "orbital"
      ? `/calculators/orbital?${params.toString()}`
      : `/calculators/manual-tig?${params.toString()}`;
  };

  return (
    <div data-testid="history-page" className="max-w-5xl mx-auto px-4 md:px-6 py-10">
      <span className="wb-tag mb-3">{lang === "sk" ? "História" : "History"}</span>
      <h1 className="font-display font-black text-4xl md:text-5xl mt-2 mb-8">
        {lang === "sk" ? "Tvoje výpočty" : "Your computations"}
      </h1>

      {loading && <div className="text-zinc-500">…</div>}

      {!loading && items.length === 0 && (
        <div className="border border-dashed border-zinc-800 p-12 text-center text-zinc-500">
          <FileText className="w-10 h-10 mx-auto mb-3 text-zinc-700" strokeWidth={1.2} />
          <p className="font-mono uppercase tracking-wider text-xs">
            {lang === "sk" ? "Žiadne uložené výpočty" : "No saved computations"}
          </p>
          <Link to="/calculators/orbital" data-testid="empty-cta" className="wb-btn-primary inline-block mt-6 px-6 py-2.5 text-xs">
            {lang === "sk" ? "Vytvoriť výpočet" : "Create one"}
          </Link>
        </div>
      )}

      <div className="space-y-px bg-zinc-800">
        {items.map((h) => {
          const Icon = h.type === "orbital" ? Gauge : Wrench;
          const tag = h.type === "orbital" ? "ORBITAL" : "TIG";
          const sub = h.type === "orbital"
            ? `${h.inputs.machine?.toUpperCase()} · ${h.inputs.material} · ⌀${h.inputs.diameter}mm × ${h.inputs.wall_thickness}mm`
            : `${h.inputs.material} · ${h.inputs.thickness}mm · ⌀${h.inputs.electrode_diameter}mm`;
          const key = h.type === "orbital"
            ? `${h.outputs.base_current_a}A · ${h.outputs.travel_speed_mm_min}mm/min`
            : `${h.outputs.current_min_a}–${h.outputs.current_max_a}A`;
          return (
            <div key={h.id} className="bg-[var(--wb-surface)] p-4 flex items-center gap-4" data-testid={`history-item-${h.id}`}>
              <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-gold-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="wb-tag !text-gold-500 !border-gold-700">{tag}</span>
                  <span className="font-display font-bold text-base text-gold-500">{key}</span>
                  <span className="text-xs text-zinc-500 font-mono">{new Date(h.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-zinc-400 mt-0.5 truncate">{sub}</div>
              </div>
              <Link to={linkFor(h)} data-testid={`history-open-${h.id}`}
                className="wb-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1">
                {lang === "sk" ? "Otvoriť" : "Open"} <ArrowRight className="w-3 h-3" />
              </Link>
              <button onClick={() => remove(h.id)} data-testid={`history-delete-${h.id}`}
                className="p-2 text-zinc-500 hover:text-red-500 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
